import { useState, useEffect } from "react";
import { Button, Text } from "@telegram-apps/telegram-ui";
import { useGameStore, selectMana } from "../../store/gameStore";
import { ManaAnimationComponent } from "./ManaAnimationComponent";
import { ManaReward } from "../../handlers/types";
import "./ManaContent.css";
import { useI18n } from "../../utils/i18n/I18nProvider";
import { StorageService } from "../../services/LocalStorageService";

export function ManaContent(): JSX.Element {
  const { t } = useI18n();
  const mana = useGameStore(selectMana);
  const [selectedMultiplier, setSelectedMultiplier] = useState<number>(1);
  const [showAnimation, setShowAnimation] = useState(false);

  // Get current mana count from store
  const currentMana = mana?.current || 0;
  const multipliers = [50, 10, 5, 1];

  const handleMultiplierSelect = (multiplier: number) => {
    if (!showAnimation) {
      setSelectedMultiplier(multiplier);
    }
  };

  const handleStart = () => {
    if (currentMana < selectedMultiplier) {
      return;
    }
    // Dismiss one-time hint for start button when it is used first time
    if (
      !StorageService.isOneTimeHintDismissed(
        StorageService.ONE_TIME_HINT_KEYS.MANA_START_BUTTON
      )
    ) {
      StorageService.dismissOneTimeHint(
        StorageService.ONE_TIME_HINT_KEYS.MANA_START_BUTTON
      );
    }
    setShowAnimation(true);
  };

  const handleAnimationComplete = async (rewards: ManaReward[]) => {
    // Simply reset the animation state and return to ManaContent
    setShowAnimation(false);

    // Refresh user data from server to keep it in sync
    // This ensures the balance is accurate for other parts of the app
    try {
      await useGameStore.getState().refreshUserData();
    } catch (error) {
      console.error("Failed to refresh user data after mana operation:", error);
      // Continue even if server refresh fails
    }
  };

  const renderFloatingItems = () => {
    const items = [
      { src: "/assets/img/ui/USDT.webp", alt: "USDT" },
      { src: "/assets/img/ui/coin.webp", alt: "Coin" },
      { src: "/assets/img/ui/revive.webp", alt: "Revive" },
      { src: "/assets/img/ui/heal.webp", alt: "Heal" },
      { src: "/assets/img/ui/EVL.webp", alt: "EVL" },
      { src: "/assets/img/ui/mana.webp", alt: "Mana" },
      { src: "/assets/img/ui/energy_full.webp", alt: "Energy" },
    ];

    return items.map((item, index) => (
      <img
        key={index}
        src={item.src}
        alt={item.alt}
        className={`mana-content__floating-item mana-content__floating-item--${
          index + 1
        }`}
      />
    ));
  };

  // Render full-screen animation component
  if (showAnimation) {
    return (
      <ManaAnimationComponent
        selectedMultiplier={selectedMultiplier}
        onComplete={handleAnimationComplete}
      />
    );
  }

  return (
    <>
      <div className="mana-content">
        <div className="mana-content__content">
          {/* Title */}
          <Text id="mana-content-title" className="mana-content__title">
            {t("mana.modal_title")}
          </Text>

          {/* USDT Prize Pool */}
          {/* <div className="mana-content__prize-pool">
            <Text className="mana-content__prize-text">
              <span className="mana-content__prize-label">
                USDT prize pool today:
              </span>
              <span className="mana-content__prize-amount">100</span>
              <img
                src="/assets/img/ui/USDT.webp"
                alt="Prize"
                className="mana-content__prize-icon"
              />
            </Text>
          </div> */}

          {/* Current Mana */}
          <div className="mana-content__current-mana">
            <span className="mana-content__mana-label">
              {t("mana.you_have")}
            </span>
            <span className="mana-content__mana-text">{currentMana}</span>
            <img
              src="/assets/img/ui/mana.webp"
              alt="Mana"
              className="mana-content__mana-icon"
            />
          </div>

          {/* Magical Cauldron Scene */}
          <div className="mana-content__scene">
            <div className="mana-content__floating-items">
              {renderFloatingItems()}
            </div>
          </div>

          {/* Start Button */}
          <div className="mana-content__start-button-wrapper">
            <Button
              onClick={handleStart}
              size="l"
              stretched
              className="mana-content__start-button"
              disabled={currentMana < selectedMultiplier}
            >
              {t("mana.start_with_multiplier", {
                multiplier: selectedMultiplier,
              })}
            </Button>
            {!StorageService.isOneTimeHintDismissed(
              StorageService.ONE_TIME_HINT_KEYS.MANA_START_BUTTON
            ) && <div className="notification-dot notification-dot--corner" />}
          </div>

          {/* Multiplier Buttons */}
          <div className="mana-content__multipliers">
            {multipliers.map((multiplier) => (
              <Button
                key={multiplier}
                onClick={() => handleMultiplierSelect(multiplier)}
                size="m"
                className={`mana-content__multiplier-button ${
                  selectedMultiplier === multiplier
                    ? "mana-content__multiplier-button--selected"
                    : ""
                }`}
                disabled={currentMana < multiplier}
              >
                X{multiplier}
              </Button>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}
